import { ListLayoutState, ListLayoutViewType, SavedFilter, TableState, KanbanState } from './list-layout.types';
import { cloneDeep } from './list-layout.utils';

const STORAGE_PREFIX = 'listLayout_';

export function getStorageKey(storageKey: string): string {
    return `${STORAGE_PREFIX}${storageKey}_state`;
}

export function createDefaultState(defaultView: ListLayoutViewType): ListLayoutState {
    return {
        activeView: defaultView,
        filters: {},
        savedFilters: [],
        tableState: {
            pageSize: 50,
            currentPage: 1,
            selectedItems: [],
        },
        kanbanState: {
            collapsedColumns: [],
        },
        showFilters: false,
    };
}

export function loadStateFromLocalStorage(storageKey: string, defaultView: ListLayoutViewType): ListLayoutState {
    try {
        const key = getStorageKey(storageKey);
        const stored = localStorage.getItem(key);

        if (!stored) {
            return createDefaultState(defaultView);
        }

        const parsed = JSON.parse(stored);

        // Validate and merge with default state
        const defaultState = createDefaultState(defaultView);
        return {
            ...defaultState,
            ...parsed,
            tableState: {
                ...defaultState.tableState,
                ...(parsed.tableState || {}),
            },
            kanbanState: {
                ...defaultState.kanbanState,
                ...(parsed.kanbanState || {}),
            },
            savedFilters: Array.isArray(parsed.savedFilters)
                ? parsed.savedFilters.map((f: any) => ({
                      ...f,
                      createdAt: new Date(f.createdAt),
                      updatedAt: f.updatedAt ? new Date(f.updatedAt) : undefined,
                  }))
                : [],
        };
    } catch (error) {
        console.error('Failed to load ListLayout state from localStorage:', error);
        return createDefaultState(defaultView);
    }
}

export function saveStateToLocalStorage(storageKey: string, state: ListLayoutState): void {
    try {
        const key = getStorageKey(storageKey);
        const toSave = cloneDeep(state);
        localStorage.setItem(key, JSON.stringify(toSave));
    } catch (error) {
        console.error('Failed to save ListLayout state to localStorage:', error);
    }
}

export function clearStateFromLocalStorage(storageKey: string): void {
    try {
        const key = getStorageKey(storageKey);
        localStorage.removeItem(key);
    } catch (error) {
        console.error('Failed to clear ListLayout state from localStorage:', error);
    }
}

export function loadSavedFilters(storageKey: string): SavedFilter[] {
    try {
        const state = loadStateFromLocalStorage(storageKey, ListLayoutViewType.Table);
        return state.savedFilters;
    } catch (error) {
        console.error('Failed to load saved filters:', error);
        return [];
    }
}

export function saveSavedFilters(storageKey: string, savedFilters: SavedFilter[]): void {
    try {
        const key = getStorageKey(storageKey);
        const stored = localStorage.getItem(key);
        const state = stored ? JSON.parse(stored) : createDefaultState(ListLayoutViewType.Table);

        state.savedFilters = savedFilters;
        localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
        console.error('Failed to save saved filters:', error);
    }
}
